'use client';

import Image from 'next/image';
import Link from 'next/link';
import { z } from 'zod';

import Heading from '~/components/atoms/Heading';
import Wrap from '~/components/atoms/Wrap';
import Media from '~/components/molecules/Media';
import TextSplitter from '~/components/molecules/TextSplitter';
import { schemas } from '~/components/organisms/slices/content-blocks/@types/schemas';
import routeGenerator from '~/lib/api/routes';

export const defaultElement: React.ElementType = 'li' as const;
export type AllowedElements = typeof defaultElement | keyof React.ElementType;

type Props<E extends AllowedElements> = {
  as?: E;
  block: z.infer<
    typeof schemas.mondrianLayout
  >['lines'][number]['blocks'][number];
};

const MondrianBlock = <E extends AllowedElements = typeof defaultElement>({
  as: asComponent,
  block,
}: Props<E>) => {
  const Component = asComponent || defaultElement;

  const resolveHref = () => {
    if (block.blockType === 'internal_link') {
      const { postObject } = block.internalLinkContent;

      switch (postObject.node.__typename) {
        case 'Page': {
          return routeGenerator.pageBySlug({
            slug: postObject.node.slug,
          });
        }

        case 'Post': {
          const mainCategory = postObject.node.categories.edges.at(0);

          if (mainCategory) {
            return routeGenerator.post({
              category: mainCategory.node.slug,
              slug: postObject.node.slug,
            });
          } else {
            return null;
          }
        }

        case 'Property': {
          const mainCategory = postObject.node.propertyTypes.edges.at(0);

          if (mainCategory) {
            return routeGenerator.property({
              category: mainCategory.node.slug,

              slug: postObject.node.slug,
            });
          } else {
            return null;
          }
        }
      }
    }

    return null;
  };

  const renderBlockContents = () => {
    switch (block.blockType) {
      case 'media': {
        return (
          <Media
            media={block.mediaContent.media}
            imageProps={{
              className: 'w-full h-auto lg:h-full object-cover',
              fill: true,
              sizes:
                block.mediaContent.media.type === 'image'
                  ? block.mediaContent.media.image?.node.sizes
                  : '',
            }}
            videoProps={{
              className: 'w-full h-full object-cover',
              autoPlay: true,
              playsInline: true,
              loop: true,
              muted: true,
            }}
            reactPlayerProps={{
              className: '!w-full !h-full !object-cover',
              playing: true,
              playsInline: true,
              loop: true,
              muted: true,
            }}
          />
        );
      }

      case 'internal_link': {
        const href = resolveHref();
        const { postObject } = block.internalLinkContent;

        return (
          <Wrap
            with={(children) => (
              <Link className="group/mondrian-block-wrap flex" href={href!}>
                {children}
              </Link>
            )}
            when={Boolean(href)}
          >
            {postObject.node.featuredImage && (
              <Image
                src={postObject.node.featuredImage.node.sourceUrl}
                alt={postObject.node.featuredImage.node.altText}
                sizes={postObject.node.featuredImage.node.sizes}
                placeholder={
                  postObject.node.featuredImage.node.blurDataUrl
                    ? 'blur'
                    : 'empty'
                }
                blurDataURL={
                  postObject.node.featuredImage.node.blurDataUrl || undefined
                }
                className="object-cover"
                fill
                unoptimized
              />
            )}

            <div className="group-hocus-within/mondrian-block-wrap:bg-neutral-800/50 absolute top-0 left-0 flex h-full w-full flex-col justify-end gap-y-5 bg-neutral-800/25 p-4 pr-10 transition-colors duration-500 lg:justify-between lg:p-20">
              <div className="flex max-w-110 flex-col gap-y-2 text-white">
                <Heading as="h2" level={2}>
                  {postObject.node.title}
                </Heading>

                <div
                  className="relative"
                  dangerouslySetInnerHTML={{
                    __html:
                      postObject.node.__typename === 'Page'
                        ? postObject.node.content
                        : postObject.node.excerpt,
                  }}
                />
              </div>

              <Heading as="p" level="xl" className="text-white">
                <TextSplitter
                  text="En savoir plus"
                  generateLetterProps={({ index }) => ({
                    className:
                      'relative inline-block lg:translate-y-1 lg:opacity-0 lg:transition-all lg:duration-700 lg:group-hocus-within/mondrian-block-wrap:translate-y-0 lg:group-hocus-within/mondrian-block-wrap:opacity-100',
                    style: { transitionDelay: `${index * 15}ms` },
                  })}
                />
              </Heading>
            </div>
          </Wrap>
        );
      }

      default: {
        return null;
      }
    }
  };

  return (
    <Component
      className="bg-brand-tertiary relative h-130 max-h-svh lg:basis-0"
      style={{ flexGrow: block.flexFactor }}
    >
      {renderBlockContents()}
    </Component>
  );
};

export default MondrianBlock;
